import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetContractorsQuery,
  useGetContractorPersonasQuery,
  useLazyGetContractorPersonasQuery,
  useSetContractorPersonasMutation,
} from './contractorsApiSlice';
import { useGetCustomerPersonasQuery } from '../customer_personas/customerPersonaApiSlice';
import CustomDropdown from '../../components/CustomDropdown';
import { Dash, ListNote, ListTable, RowAction, RowCheck, SaveBar, Tag, stack } from '../../components/listPage';

/** 파트너 상세 › 고객 페르소나: the customers this 파트너's trainees are offered. Assignment is
 *  exclusive, so this checklist IS their roleplay screen — edited as a set and saved at once,
 *  so trainees never see a half-edited lineup. */
const ContractorPersonasTab = ({ contractorId, isMobile }) => {
  const navigate = useNavigate();
  const { data: personasData, isLoading } = useGetCustomerPersonasQuery({ scope: 'all', page_size: 1000 });
  const { data: assigned } = useGetContractorPersonasQuery(contractorId);
  const { data: contractorsData } = useGetContractorsQuery({ page_size: 1000 });
  const [fetchOther] = useLazyGetContractorPersonasQuery();
  const [save, { isLoading: saving }] = useSetContractorPersonasMutation();

  const [selected, setSelected] = useState(null);
  const [copyFrom, setCopyFrom] = useState('');
  const saved = useMemo(() => new Set(assigned?.persona_ids || []), [assigned]);
  useEffect(() => { if (assigned) setSelected(new Set(assigned.persona_ids)); }, [assigned]);

  // Dynamic personas belong to one trainee; deactivated ones are shown to nobody.
  const pool = (personasData?.results || []).filter(p => !p.is_dynamic && !p.deactivated_at);
  const picked = selected || saved;
  const dirty = picked.size !== saved.size || [...picked].some(id => !saved.has(id));
  const toggle = id => setSelected(prev => {
    const next = new Set(prev || saved);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const copyOptions = (contractorsData?.results || [])
    .filter(c => c.id !== contractorId)
    .map(c => ({ key: c.id, value: c.id, text: `${c.name} (${c.persona_count}명)` }));
  const copy = async id => {
    setCopyFrom(id || '');
    if (!id) return;
    const r = await fetchOther(id).unwrap();
    setSelected(new Set(r.persona_ids));
  };

  const label = p => (p.int_id ? `${p.int_id}번 고객` : p.name);
  const others = p => (p.contractors_info || []).filter(c => c.id !== contractorId);
  const check = p => <RowCheck label={`${label(p)} 선택`} checked={picked.has(p.id)} onChange={() => toggle(p.id)} />;

  return (
    <div style={stack}>
      <ListNote>
        체크한 고객만 이 파트너 훈련생의 롤플레이 화면에 나타납니다. 변경 사항은 <b>저장</b>해야 반영됩니다.
      </ListNote>
      {picked.size === 0 && !isLoading && (
        <ListNote tone="amber">선택된 고객이 없습니다. 이대로라면 이 파트너 훈련생에게는 연습할 고객이 보이지 않습니다.</ListNote>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <CustomDropdown
          searchable
          placeholder="다른 파트너에서 복사"
          options={copyOptions}
          value={copyFrom}
          onChange={copy}
          icon="copy outline"
          style={isMobile ? { width: '100%' } : { minWidth: '240px', flex: '0 0 auto' }}
        />
        <span style={{ fontSize: '13px', color: '#86868b' }}>
          <b style={{ color: '#1d1d1f' }}>{picked.size}</b> / {pool.length}명 선택
        </span>
      </div>
      <ListTable
        isMobile={isMobile}
        loading={isLoading || !assigned}
        rows={pool}
        empty="배정할 수 있는 페르소나가 없습니다."
        onRowClick={p => toggle(p.id)}
        columns={[
          { key: 'check', label: '', render: check },
          { key: 'no', label: '번호', nowrap: true, render: p => <span style={{ fontWeight: 600 }}>{label(p)}</span> },
          { key: 'purpose', label: '설명', render: p => <div title={p.purpose_of_visit} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '520px', color: '#424245' }}>{p.purpose_of_visit}</div> },
          {
            key: 'others', label: '다른 파트너',
            render: p => (others(p).length
              ? <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{others(p).map(c => <Tag key={c.id}>{c.name}</Tag>)}</div>
              : <Dash />),
          },
          { key: 'open', label: '', align: 'right', render: p => <RowAction icon="external alternate" title="페르소나 상세" onClick={() => navigate(`/customerpersonas/${p.id}`)} /> },
        ]}
        card={{
          title: label,
          tags: p => [<span key="c" style={{ display: 'flex' }}>{check(p)}</span>],
          meta: p => [others(p).length ? `다른 파트너: ${others(p).map(c => c.name).join(', ')}` : '다른 파트너 없음'],
          body: p => p.purpose_of_visit,
        }}
      />
      <SaveBar disabled={!dirty} saving={saving} onSave={() => save({ id: contractorId, persona_ids: [...picked] })} />
    </div>
  );
};

export default ContractorPersonasTab;
